import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  Activity,
  AlertCircle,
  Loader2,
  LockKeyhole,
  RotateCcw,
  Save,
  ShieldCheck,
  Sparkles,
  Thermometer,
  Trash2,
  Unlock,
  X,
  ZoomIn,
  ZoomOut
} from 'lucide-react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { supabase } from '@/utils/supabase/client';
import { functionsBaseUrl } from '/utils/supabase/info';
import type { AnomalyType, Severity, ValidationResult } from '@/app/utils/vision-biology';
import { DEMO_USER_EMAIL, hideDemoAlert, saveDemoAlertValidation } from '@/app/utils/vision-demo-data';

const AI_DISCLAIMER = 'Análise por IA · carece de revisão de patologista';

interface VisionAiAnalysis {
  description: string;
  suspectedFindings: string[];
  differentialHypotheses: string[];
  confidence: number | null;
  recommendedNextSteps: string;
  operationalAssessment?: string;
  analysisTags?: string[];
  validated?: boolean | null;
}

const formatAiAnalysisNotes = (analysis: VisionAiAnalysis): string => {
  const lines = [`[${AI_DISCLAIMER}]`, ''];
  if (analysis.description) {
    lines.push(analysis.description, '');
  }
  if (analysis.suspectedFindings.length) {
    lines.push(`Achados sugestivos: ${analysis.suspectedFindings.join('; ')}`);
  }
  if (analysis.differentialHypotheses.length) {
    lines.push(`Hipóteses (não diagnósticas): ${analysis.differentialHypotheses.join('; ')}`);
  }
  if (analysis.confidence !== null) {
    lines.push(`Confiança visual: ${Math.round(analysis.confidence * 100)}%`);
  }
  if (analysis.recommendedNextSteps) {
    lines.push('', `Próximos passos sugeridos: ${analysis.recommendedNextSteps}`);
  }
  lines.push('', 'A decisão clínica final é da responsabilidade do patologista.');
  return lines.join('\n').trim();
};

const normalizeAiAnalysis = (raw: any): VisionAiAnalysis | null => {
  if (!raw || typeof raw !== 'object') return null;
  const description = typeof raw.description === 'string' ? raw.description.trim() : '';
  const suspectedFindings = Array.isArray(raw.suspectedFindings) ? raw.suspectedFindings.map(String) : [];
  const differentialHypotheses = Array.isArray(raw.differentialHypotheses) ? raw.differentialHypotheses.map(String) : [];
  const confidence = typeof raw.confidence === 'number' ? raw.confidence : null;
  const recommendedNextSteps = typeof raw.recommendedNextSteps === 'string' ? raw.recommendedNextSteps.trim() : '';
  const operationalAssessment = typeof raw.operationalAssessment === 'string' ? raw.operationalAssessment.trim() : '';
  const analysisTags = Array.isArray(raw.analysisTags) ? raw.analysisTags.map(String) : [];
  const validated = typeof raw.validated === 'boolean' ? raw.validated : null;

  if (!description && !suspectedFindings.length && !differentialHypotheses.length && !recommendedNextSteps && !operationalAssessment && !analysisTags.length) {
    return null;
  }

  return {
    description,
    suspectedFindings,
    differentialHypotheses,
    confidence,
    recommendedNextSteps,
    operationalAssessment,
    analysisTags,
    validated
  };
};

type OverlayBox = { x: number; y: number; width: number; height: number; source: 'yolo' | 'gemini' };

const normalizeOverlayBox = (raw: any, source: OverlayBox['source'], imageSize?: { width: number; height: number } | null): OverlayBox | null => {
  if (!raw) return null;
  const arrayValue = Array.isArray(raw) ? raw : raw.bbox ?? raw.box ?? raw.boundingBox ?? raw.bounding_box ?? raw.bbox_2d;
  const value = Array.isArray(arrayValue)
    ? source === 'gemini' && arrayValue.length >= 4 && arrayValue.every((item) => Number.isFinite(Number(item))) && Math.max(...arrayValue.map(Number)) > 1
      ? {
        x: Number(arrayValue[1]),
        y: Number(arrayValue[0]),
        width: Number(arrayValue[3]) - Number(arrayValue[1]),
        height: Number(arrayValue[2]) - Number(arrayValue[0])
      }
      : { x: arrayValue[0], y: arrayValue[1], width: arrayValue[2], height: arrayValue[3] }
    : {
      x: raw.x ?? raw.left ?? raw.xmin ?? raw.x_min,
      y: raw.y ?? raw.top ?? raw.ymin ?? raw.y_min,
      width: raw.width ?? raw.w,
      height: raw.height ?? raw.h
    };
  let x = Number(value.x);
  let y = Number(value.y);
  let width = Number(value.width ?? ((raw.xmax ?? raw.x_max ?? raw.right) != null ? Number(raw.xmax ?? raw.x_max ?? raw.right) - x : undefined));
  let height = Number(value.height ?? ((raw.ymax ?? raw.y_max ?? raw.bottom) != null ? Number(raw.ymax ?? raw.y_max ?? raw.bottom) - y : undefined));
  if (![x, y, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return null;
  const maxValue = Math.max(x, y, width, height, x + width, y + height);
  if (source === 'gemini' && maxValue > 1 && maxValue <= 1000) {
    x /= 1000;
    width /= 1000;
    y /= 1000;
    height /= 1000;
  } else if ((x > 1 || y > 1 || width > 1 || height > 1) && imageSize?.width && imageSize?.height) {
    x /= imageSize.width;
    width /= imageSize.width;
    y /= imageSize.height;
    height /= imageSize.height;
  }
  return {
    x: Math.max(0, Math.min(1, x)),
    y: Math.max(0, Math.min(1, y)),
    width: Math.max(0.01, Math.min(1, width)),
    height: Math.max(0.01, Math.min(1, height)),
    source
  };
};

interface VisionAlert {
  id: string;
  case_id?: string;
  alert_id?: string;
  detection_id?: string;
  capture_id?: string;
  kit_id?: string;
  client_id?: string;
  project_id?: string;
  userEmail?: string;
  imageUrl?: string;
  image_url?: string;
  species?: string;
  biological_phase?: string;
  detected_at?: string;
  detectedAt?: string;
  status?: string;
  severity?: Severity | string | null;
  alert_type?: string | null;
  alertType?: string | null;
  model_confidence?: number | string | null;
  metadata?: Record<string, any>;
  aiAnalysis?: {
    confidence?: number;
    detectedIssues?: string[];
    recommendation?: string;
  };
  problemName?: string;
  problemDescription?: string;
  proposedSolution?: string;
  validationResult?: ValidationResult | 'confirmed' | 'rejected' | 'false_positive';
  validation_result?: ValidationResult;
  anomalyType?: AnomalyType;
  anomaly_type?: AnomalyType;
  validationNotes?: string;
  validation_notes?: string;
  labObservations?: string;
  createdAt?: string;
  created_at?: string;
  validatedAt?: string;
  validated_at?: string;
  validated_by?: string;
  validatedBy?: string;
  validated_by_email?: string;
  validatedByEmail?: string;
  validated_by_name?: string;
  validatedByName?: string;
}

interface ClassifyAlertDialogProps {
  isOpen: boolean;
  onClose: () => void;
  alert: VisionAlert;
  apiUrl: string;
  authToken: string;
  publicAnonKey: string;
  onUpdate: () => void;
  bulkTargets?: BulkValidationTarget[];
  bulkLabel?: string;
}

export interface BulkValidationTarget {
  id: string;
  case_id?: string | null;
  alert_id?: string | null;
  detection_id?: string | null;
  capture_id?: string | null;
  sensor_reading_id?: string | null;
  kit_id?: string | null;
}

interface ValidationUser {
  user_id: string;
  auth_user_id?: string | null;
  client_id?: string | null;
  project_id?: string | null;
  name?: string | null;
  username?: string | null;
  email: string;
  role?: string | null;
  is_active?: boolean | null;
}

const validationOptions: Array<{ value: ValidationResult; label: string }> = [
  { value: 'confirmed_anomaly', label: 'Confirmar anomalia' },
  { value: 'rejected_alert', label: 'Rejeitar alerta' },
  { value: 'inconclusive', label: 'Inconclusivo' }
];

const anomalyOptions: Array<{ value: AnomalyType; label: string }> = [
  { value: 'pseudoalbinism', label: 'pseudoalbinismo' },
  { value: 'deformation', label: 'deformação' },
  { value: 'lesion_spot', label: 'lesão/mancha' },
  { value: 'mortality', label: 'mortalidade' },
  { value: 'abnormal_behavior', label: 'comportamento anómalo' },
  { value: 'other', label: 'outro' },
  { value: 'not_applicable', label: 'não aplicável' }
];

const severityOptions: Array<{ value: Severity; label: string }> = [
  { value: 'low', label: 'baixa' },
  { value: 'medium', label: 'média' },
  { value: 'high', label: 'alta' },
  { value: 'critical', label: 'crítica' }
];

type ValidationStateTag = ValidationResult | 'pending';

const validationStateLabel: Record<ValidationStateTag, string> = {
  pending: 'Pendente',
  confirmed_anomaly: 'Confirmado',
  rejected_alert: 'Rejeitado',
  inconclusive: 'Inconclusivo'
};

const validationStateTone: Record<ValidationStateTag, string> = {
  pending: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-300/28 dark:bg-amber-500/14 dark:text-amber-100',
  confirmed_anomaly: 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-300/28 dark:bg-emerald-500/14 dark:text-emerald-100',
  rejected_alert: 'border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-400/24 dark:bg-slate-500/16 dark:text-slate-100',
  inconclusive: 'border-cyan-300 bg-cyan-50 text-cyan-800 dark:border-cyan-300/28 dark:bg-cyan-500/14 dark:text-cyan-100'
};

const severityLabel: Record<Severity, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  critical: 'Crítica'
};

const severityTone: Record<Severity, string> = {
  low: 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-300/24 dark:bg-emerald-500/12 dark:text-emerald-100',
  medium: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-300/24 dark:bg-amber-500/12 dark:text-amber-100',
  high: 'border-orange-300 bg-orange-50 text-orange-800 dark:border-orange-300/24 dark:bg-orange-500/12 dark:text-orange-100',
  critical: 'border-red-300 bg-red-50 text-red-800 dark:border-red-300/24 dark:bg-red-500/12 dark:text-red-100'
};

const segmentedGridCols: Record<number, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4'
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return 'A confirmar';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'A confirmar';
  return date.toLocaleString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const normalizeText = (value: unknown) =>
  String(value ?? '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/-+/g, '_');

const normalizeValidationResult = (value: unknown): ValidationResult | '' => {
  const normalized = normalizeText(value);
  if (normalized === 'confirmed_anomaly' || normalized === 'confirmed') return 'confirmed_anomaly';
  if (normalized === 'rejected_alert' || normalized === 'rejected' || normalized === 'false_positive') return 'rejected_alert';
  if (normalized === 'inconclusive') return 'inconclusive';
  return '';
};

const normalizeAnomalyType = (value: unknown): AnomalyType | '' => {
  const normalized = normalizeText(value);
  const option = anomalyOptions.find((item) => normalizeText(item.value) === normalized || normalizeText(item.label) === normalized);
  return option?.value ?? '';
};

const normalizeSeverity = (value: unknown): Severity => {
  const normalized = normalizeText(value);
  if (normalized === 'critical') return 'critical';
  if (normalized === 'high') return 'high';
  if (normalized === 'low') return 'low';
  return 'medium';
};

const getMeta = (alert: VisionAlert, keys: string[]) => {
  for (const key of keys) {
    const value = alert.metadata?.[key] ?? (alert as Record<string, any>)[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

const formatSensorUnit = (value: unknown) => {
  const unit = String(value ?? '').trim();
  if (!unit) return '';
  if (/^(c|celsius|ºc|°c)$/i.test(unit)) return ' °C';
  if (/^(mg_l|mg\/l)$/i.test(unit)) return ' mg/L';
  return ` ${unit}`;
};

const formatSensorValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(String(value).replace(',', '.'));
  if (Number.isFinite(numeric)) {
    return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(2).replace(/\.?0+$/, '');
  }
  return String(value).trim();
};

const sensorLabelFrom = (value: unknown) => {
  const normalized = normalizeText(value);
  if (normalized.includes('temp')) return 'Temperatura';
  if (normalized.includes('ph')) return 'pH';
  if (normalized.includes('oxygen') || normalized.includes('oxigen') || normalized.includes('o2')) return 'Oxigénio dissolvido';
  if (normalized.includes('redox') || normalized.includes('orp')) return 'Redox / ORP';
  if (normalized.includes('salin')) return 'Salinidade';
  return 'Leitura operacional';
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const VALIDATOR_ROLES = new Set(['system_administrator', 'client_admin', 'admin', 'supervisor', 'operator']);
const VALIDATION_UNLOCK_SESSION_KEY = 'aql_vision_validation_unlock_v1';

type ValidationUnlockSession = {
  user_id?: string;
  auth_user_id?: string;
  email?: string;
  unlocked_at?: string;
};

const pickString = (...values: unknown[]) => {
  const found = values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
  return found == null ? null : String(found).trim();
};

const pickUuid = (...values: unknown[]) => {
  const value = pickString(...values);
  return value && UUID_PATTERN.test(value) ? value : null;
};

const normalizeValidationUser = (row: Record<string, any>): ValidationUser => ({
  user_id: String(row.user_id ?? row.id ?? ''),
  auth_user_id: pickString(row.auth_user_id),
  client_id: pickString(row.client_id),
  project_id: pickString(row.project_id),
  name: pickString(row.full_name, row.name, row.user_name, row.username),
  username: pickString(row.username, row.user_name),
  email: String(row.email ?? ''),
  role: String(row.role ?? 'viewer'),
  is_active: Boolean(row.is_active ?? true)
});

const readValidationUnlockSession = (): ValidationUnlockSession | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(VALIDATION_UNLOCK_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ValidationUnlockSession;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeValidationUnlockSession = (user: ValidationUser) => {
  if (typeof window === 'undefined') return;
  const payload: ValidationUnlockSession = {
    user_id: user.user_id,
    auth_user_id: user.auth_user_id ?? undefined,
    email: user.email?.toLowerCase?.() ?? undefined,
    unlocked_at: new Date().toISOString()
  };
  window.sessionStorage.setItem(VALIDATION_UNLOCK_SESSION_KEY, JSON.stringify(payload));
};

const clearValidationUnlockSession = () => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(VALIDATION_UNLOCK_SESSION_KEY);
};

const isUserUnlockedInSession = (user: ValidationUser | null | undefined) => {
  if (!user) return false;
  const snapshot = readValidationUnlockSession();
  if (!snapshot) return false;
  const sameUserId = snapshot.user_id && snapshot.user_id === user.user_id;
  const sameAuthUserId = snapshot.auth_user_id && user.auth_user_id && snapshot.auth_user_id === user.auth_user_id;
  const sameEmail = snapshot.email && user.email && snapshot.email === user.email.toLowerCase();
  return Boolean(sameUserId || sameAuthUserId || sameEmail);
};

const validatorDisplayName = (user: ValidationUser | null | undefined) =>
  user ? pickString(user.name, user.username, user.email) ?? 'Utilizador' : 'Utilizador';

const validatorRoleLabel = (role: unknown) => {
  const normalized = normalizeText(role);
  if (normalized === 'system_administrator') return 'system admin';
  if (normalized === 'client_admin') return 'client admin';
  if (normalized === 'supervisor') return 'supervisor/a';
  return normalized || 'sem role';
};

const matchesAlertScope = (user: ValidationUser, alert: VisionAlert) => {
  const role = normalizeText(user.role);
  if (role === 'system_administrator' || role === 'admin') return true;

  const alertClientId = pickString(getMeta(alert, ['client_id', 'clientId']));
  const alertProjectId = pickString(getMeta(alert, ['project_id', 'projectId']));

  if (user.client_id && alertClientId && user.client_id !== alertClientId) return false;
  if (user.project_id && alertProjectId && user.project_id !== alertProjectId) return false;

  return true;
};

export function ClassifyAlertDialog({
  isOpen,
  onClose,
  alert,
  authToken,
  publicAnonKey,
  onUpdate,
  bulkTargets = [],
  bulkLabel
}: ClassifyAlertDialogProps) {
  const [validationResult, setValidationResult] = useState<ValidationResult | ''>('');
  const [anomalyType, setAnomalyType] = useState<AnomalyType | ''>('');
  const [severity, setSeverity] = useState<Severity>('medium');
  const [validationNotes, setValidationNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [analyzingWithAI, setAnalyzingWithAI] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validatorUsers, setValidatorUsers] = useState<ValidationUser[]>([]);
  const [validatorUserId, setValidatorUserId] = useState('');
  const [validationUnlocked, setValidationUnlocked] = useState(false);
  const [validatorLoading, setValidatorLoading] = useState(false);
  const [unlockPromptOpen, setUnlockPromptOpen] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const [aiAnalysisSummary, setAiAnalysisSummary] = useState<VisionAiAnalysis | null>(null);
  const autoAnalyzedRef = useRef<string | null>(null);

  const isDemoMode = authToken.startsWith('demo_');
  const imageUrl = alert.imageUrl ?? alert.image_url ?? '';
  const overlayBoxes = (() => {
    const metadata = alert.metadata ?? {};
    const yolo = normalizeOverlayBox(metadata.yolo_bbox ?? metadata.gemini_analysis?.yolo_bbox ?? metadata.detection?.bbox, 'yolo', imageSize);
    return [yolo].filter((box): box is OverlayBox => Boolean(box));
  })();
  const sensorType = pickString(
    alert.metadata?.sensor_type,
    alert.metadata?.sensorType,
    alert.metadata?.sensor,
    alert.metadata?.metric,
    alert.alert_type,
    alert.alertType
  );
  const sensorLabel = sensorLabelFrom(sensorType);
  const sensorValue = formatSensorValue(getMeta(alert, [
    'sensor_value',
    'value',
    'calibrated_value',
    'raw_value',
    'reading_value',
    'last_value'
  ]));
  const sensorUnit = formatSensorUnit(getMeta(alert, ['unit', 'sensor_unit']));
  const sensorOrigin = pickString(
    alert.metadata?.sensor_name,
    alert.metadata?.sensor_label,
    alert.metadata?.kit_name,
    alert.metadata?.device_name,
    alert.kit_id
  ) ?? 'Sensor ambiental';
  const sensorOriginLabel = /^temp(?:eratura)?$/i.test(sensorOrigin.trim())
    ? 'Sensor de temperatura'
    : sensorOrigin;
  const sensorThreshold = formatSensorValue(getMeta(alert, [
    'threshold',
    'threshold_description',
    'rule_threshold',
    'limit',
    'limit_value',
    'target_value'
  ]));
  const sensorDescription = pickString(
    alert.problemDescription,
    alert.metadata?.rule_message,
    alert.metadata?.message,
    alert.metadata?.description,
    alert.proposedSolution
  );
  const biologicalRangeMatch = sensorDescription?.match(
    /(?:alvo biol[oó]gico|intervalo(?: operacional)?|gama(?: operacional)?)\s*:?\s*(-?\d+(?:[.,]\d+)?)\s*[–-]\s*(-?\d+(?:[.,]\d+)?)/i
  );
  const numericSensorValue = sensorValue === null ? null : Number(sensorValue.replace(',', '.'));
  const biologicalRange = biologicalRangeMatch
    ? {
      minimum: Number(biologicalRangeMatch[1].replace(',', '.')),
      maximum: Number(biologicalRangeMatch[2].replace(',', '.')),
      label: `${biologicalRangeMatch[1]}–${biologicalRangeMatch[2]}${sensorUnit}`
    }
    : null;
  const sensorIsOutsideRange = Boolean(
    biologicalRange &&
    numericSensorValue !== null &&
    Number.isFinite(numericSensorValue) &&
    (numericSensorValue < biologicalRange.minimum || numericSensorValue > biologicalRange.maximum)
  );
  const sensorDeviation = biologicalRange && numericSensorValue !== null && Number.isFinite(numericSensorValue)
    ? numericSensorValue < biologicalRange.minimum
      ? {
        value: biologicalRange.minimum - numericSensorValue,
        direction: 'abaixo do mínimo'
      }
      : numericSensorValue > biologicalRange.maximum
        ? {
          value: numericSensorValue - biologicalRange.maximum,
          direction: 'acima do máximo'
        }
        : null
    : null;
  const timestamp = String(alert.detected_at ?? alert.detectedAt ?? alert.metadata?.Timestamp ?? alert.created_at ?? alert.createdAt ?? '');
  const tank = String(getMeta(alert, ['tank', 'tankName', 'location', 'camera']) ?? 'A confirmar');
  const species = String(alert.species ?? alert.metadata?.species ?? alert.metadata?.fishSpecies ?? 'A confirmar');
  const phase = String(alert.biological_phase ?? alert.metadata?.biological_phase ?? alert.metadata?.phase ?? 'A confirmar');
  const displayPhase = /^adult$/i.test(phase.trim()) ? 'Adulto' : phase;
  const alertId = pickUuid(alert.alert_id, alert.id);
  const caseId = pickUuid(alert.case_id, alert.id);
  const sensorReadingId = pickUuid(
    alert.metadata?.reading_id,
    alert.metadata?.sensor_reading_id,
    alert.metadata?.readingId
  );
  const detectionId = pickUuid(alert.detection_id, alert.metadata?.detection_id, alert.metadata?.detectionId);
  const captureId = pickUuid(alert.capture_id, alert.metadata?.capture_id, alert.metadata?.captureId);
  const alertRecordKey = String(alert.id ?? alert.alert_id ?? alert.case_id ?? '');
  const alertScopeKey = [
    pickString(alert.client_id, getMeta(alert, ['client_id', 'clientId'])),
    pickString(alert.project_id, getMeta(alert, ['project_id', 'projectId']))
  ].join(':');
  const selectedValidator = validatorUsers.find((user) => user.user_id === validatorUserId) ?? null;
  const isBulkMode = bulkTargets.length > 0;
  const effectiveBulkTargets = isBulkMode
    ? bulkTargets
    : [{
      id: alertRecordKey || caseId || alertId || sensorReadingId || 'alert',
      case_id: caseId,
      alert_id: alertId,
      detection_id: detectionId,
      capture_id: captureId,
      sensor_reading_id: caseId ? null : sensorReadingId,
      kit_id: alert.kit_id ?? alert.metadata?.kit_id ?? alert.metadata?.kitId ?? null
    }];
  const canEditValidation = Boolean(selectedValidator && validationUnlocked && !validatorLoading);
  const previousValidatorLabel = pickString(
    alert.validatedByName,
    alert.validated_by_name,
    alert.validatedByEmail,
    alert.validated_by_email,
    alert.validated_by || alert.validatedBy ? validatorDisplayName(selectedValidator) : null
  ) ?? 'utilizador';
  const hasPreviousValidation = Boolean(alert.validatedByEmail || alert.validated_by_email || alert.validated_at || alert.validatedAt);
  const visualIndicatorCount = overlayBoxes.length;
  const isPhysicoChemicalAlert = !imageUrl && (sensorLabel !== 'Leitura operacional' || sensorValue !== null || sensorThreshold !== null);
  const initialValidationResult = normalizeValidationResult(alert.validation_result ?? alert.validationResult);
  const activeValidationResult = validationResult || initialValidationResult;
  const validationState: ValidationStateTag = activeValidationResult || 'pending';
  const operationModeLabel = imageUrl ? 'Captura visual' : 'Leitura ambiental';
  const saveDisabledReason = saving || deleting
    ? 'A processar operação...'
    : !selectedValidator
      ? 'Seleciona o responsável pela validação.'
      : !validationUnlocked
        ? 'Desbloqueia a validação para editar e guardar.'
        : null;
  const deleteDisabledReason = !selectedValidator
    ? 'Seleciona o responsável para permitir eliminação.'
    : !validationUnlocked
      ? 'Desbloqueia a validação com password para eliminar.'
      : null;

  useEffect(() => {
    if (!isOpen) {
      autoAnalyzedRef.current = null;
      return;
    }
    const nextValidation = normalizeValidationResult(alert.validation_result ?? alert.validationResult);
    const nextAiAnalysis = normalizeAiAnalysis(
      alert.metadata?.gemini_analysis?.analysis ??
      alert.metadata?.ai_analysis ??
      alert.metadata?.gemini_analysis
    );
    const incomingAnomalyType = normalizeAnomalyType(alert.anomaly_type ?? alert.anomalyType);
    setValidationResult(nextValidation);
    setAnomalyType(
      isPhysicoChemicalAlert
        ? 'not_applicable'
        : incomingAnomalyType || (nextValidation === 'rejected_alert' ? 'not_applicable' : '')
    );
    setSeverity(normalizeSeverity(alert.severity ?? alert.metadata?.severity));
    const initialNotes = alert.validation_notes ?? alert.validationNotes ?? alert.labObservations ?? '';
    setValidationNotes(initialNotes);
    setAiAnalysisSummary(nextAiAnalysis);
    setValidationUnlocked(false);
    setUnlockPromptOpen(false);
    setUnlockPassword('');
    setError(null);

    if (alertRecordKey && imageUrl && !initialNotes.trim() && autoAnalyzedRef.current !== alertRecordKey) {
      autoAnalyzedRef.current = alertRecordKey;
      void runAiAnalysis({ silent: true });
    }
  // runAiAnalysis is intentionally omitted: it's recreated each render and we only
  // want this effect to fire when the alert or open state changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertRecordKey, isOpen, isPhysicoChemicalAlert]);

  useEffect(() => {
    if (!isOpen) return;
    setValidationUnlocked(isUserUnlockedInSession(selectedValidator));
  }, [
    isOpen,
    selectedValidator?.user_id,
    selectedValidator?.auth_user_id,
    selectedValidator?.email
  ]);

  useEffect(() => {
    if (!isOpen || !isPhysicoChemicalAlert) return;
    setAnomalyType((current) => (current === 'not_applicable' ? current : 'not_applicable'));
  }, [isOpen, isPhysicoChemicalAlert]);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    const previousOverscrollBehavior = document.body.style.overscrollBehavior;
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'contain';
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.overscrollBehavior = previousOverscrollBehavior;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let active = true;

    const applyUsers = (users: ValidationUser[]) => {
      const sortedUsers = [...users].sort((first, second) =>
        validatorDisplayName(first).localeCompare(validatorDisplayName(second))
      );

      if (!active) return;

      setValidatorUsers(sortedUsers);

      const previousUserId = pickString(alert.validated_by, alert.validatedBy);
      const previousEmail = pickString(alert.validatedByEmail, alert.validated_by_email);
      const storedUserId = localStorage.getItem('aql_user_id') || localStorage.getItem('aql_user_uuid') || '';
      const storedEmail = localStorage.getItem('aql_user_email') || localStorage.getItem('visionAlertsEmail') || '';
      const preferred = sortedUsers.find((user) => storedUserId && (user.user_id === storedUserId || user.auth_user_id === storedUserId))
        ?? sortedUsers.find((user) => storedEmail && user.email.toLowerCase() === storedEmail.toLowerCase())
        ?? sortedUsers.find((user) => user.user_id === previousUserId || user.auth_user_id === previousUserId)
        ?? sortedUsers.find((user) => previousEmail && user.email.toLowerCase() === previousEmail.toLowerCase())
        ?? sortedUsers[0]
        ?? null;

      setValidatorUserId(preferred?.user_id ?? '');
      setValidationUnlocked(isUserUnlockedInSession(preferred));
    };

    const loadValidators = async () => {
      setValidatorLoading(true);
      setValidatorUsers([]);
      setValidatorUserId('');

      try {
        if (isDemoMode) {
          applyUsers([
            {
              user_id: 'demo-validator-operator',
              name: 'Operador Demo',
              username: 'operador.demo',
              email: DEMO_USER_EMAIL,
              role: 'operator',
              is_active: true
            }
          ]);
          return;
        }

        const { data: sessionData } = await supabase.auth.getSession();
        const sessionUser = sessionData.session?.user ?? null;
        const currentAuthUserId = sessionUser?.id ?? '';
        const currentEmail = sessionUser?.email ?? '';

        const { data, error: usersError } = await supabase
          .from('aql_users')
          .select('*')
          .eq('is_active', true)
          .in('role', Array.from(VALIDATOR_ROLES));

        if (usersError) throw usersError;

        const users = (data ?? [])
          .map((row) => normalizeValidationUser(row as Record<string, any>))
          .filter((user) => user.user_id && user.email && user.is_active !== false)
          .filter((user) => matchesAlertScope(user, alert));

        const sessionUserMatch = users.find((user) =>
          (currentAuthUserId && user.auth_user_id === currentAuthUserId) ||
          (currentEmail && user.email.toLowerCase() === currentEmail.toLowerCase())
        );

        if (sessionUserMatch) {
          localStorage.setItem('aql_user_id', sessionUserMatch.user_id);
          if (sessionUserMatch.auth_user_id) localStorage.setItem('aql_user_uuid', sessionUserMatch.auth_user_id);
          localStorage.setItem('aql_user_email', sessionUserMatch.email);
        }

        applyUsers(users);
      } catch (err) {
        if (!active) return;
        setValidatorUsers([]);
        setValidatorUserId('');
        setError(err instanceof Error ? err.message : 'Não foi possível carregar os utilizadores autorizados.');
      } finally {
        if (active) setValidatorLoading(false);
      }
    };

    loadValidators();

    return () => {
      active = false;
    };
  }, [
    alertId,
    caseId,
    alertScopeKey,
    isDemoMode,
    isOpen
  ]);

  if (!isOpen) return null;

  const parseResponsePayload = async (response: Response) => {
    const raw = await response.text();
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch {
      return { raw };
    }
  };

  const runAiAnalysis = async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!imageUrl) {
      if (!silent) setError('Sem imagem disponível para análise.');
      return;
    }

    setAnalyzingWithAI(true);
    if (!silent) setError(null);

    try {
      if (isDemoMode) {
        await new Promise((resolve) => window.setTimeout(resolve, 450));
        const demoAnalysis: VisionAiAnalysis = {
          description: alert.aiAnalysis?.recommendation
            ?? 'Imagem demo: sinais visíveis compatíveis com lesão cutânea localizada e ligeira descoloração lateral. Necessária confirmação visual em laboratório.',
          suspectedFindings: alert.aiAnalysis?.detectedIssues?.slice(0, 3) ?? ['mancha cutânea localizada', 'descoloração lateral'],
          differentialHypotheses: ['lesão traumática', 'ulceração precoce', 'infeção bacteriana cutânea'],
          confidence: alert.aiAnalysis?.confidence ?? 0.72,
          recommendedNextSteps: 'Confirmar visualmente, cruzar com parâmetros ambientais e validar com patologista.',
          operationalAssessment: 'A IA visual sinaliza um peixe com alteração localizada e recomenda confirmação humana antes de fechar o caso.',
          analysisTags: ['suspeita_visual', 'lesao_localizada', 'revisao_humana'],
          validated: false
        };
        setAiAnalysisSummary(demoAnalysis);
        if (!validationNotes.trim()) {
          setValidationNotes(formatAiAnalysisNotes(demoAnalysis));
        }
        if (!anomalyType) setAnomalyType('lesion_spot');
        return;
      }

      const projectId = pickString(alert.project_id, alert.metadata?.project_id, alert.metadata?.projectId);
      const configUrl = projectId
        ? `${functionsBaseUrl}/vision-settings/copilot-config?project_id=${encodeURIComponent(projectId)}`
        : `${functionsBaseUrl}/vision-settings/copilot-config`;
      const configResponse = await fetch(configUrl, {
        headers: {
          Authorization: `Bearer ${publicAnonKey}`,
          apikey: publicAnonKey
        }
      }).catch(() => null);
      const configPayload = configResponse?.ok ? await parseResponsePayload(configResponse) : {};
      const copilotConfig = configPayload?.config ?? null;

      const rawYoloConfidence = Number(alert.model_confidence ?? alert.metadata?.yolo_confidence ?? alert.metadata?.confidence ?? 0) || null;
      const yoloConfidence = rawYoloConfidence && rawYoloConfidence > 1 ? rawYoloConfidence / 100 : rawYoloConfidence;
      const yoloBbox = alert.metadata?.yolo_bbox ?? alert.metadata?.gemini_analysis?.yolo_bbox ?? alert.metadata?.detection?.bbox ?? null;
      const yoloLabel = alert.alert_type ?? alert.alertType ?? alert.metadata?.label ?? alert.metadata?.detection?.label ?? null;

      const response = await fetch(`${functionsBaseUrl}/vision-analyze-image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${publicAnonKey}`,
          apikey: publicAnonKey,
          'X-Session-Token': authToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          imageUrl,
          species,
          phase,
          tank,
          alertType: yoloLabel,
          suspectPercent: yoloConfidence === null ? null : yoloConfidence * 100,
          copilotConfig,
          yolo: {
            detected: Boolean(yoloBbox || yoloConfidence || yoloLabel),
            confidence: yoloConfidence,
            bbox: yoloBbox,
            label: yoloLabel
          }
        })
      });

      const data = await parseResponsePayload(response);
      if (!response.ok) {
        throw new Error(data?.error || data?.message || 'Erro ao analisar com IA');
      }

      const raw = data?.analysis ?? {};
      const analysis = normalizeAiAnalysis(raw);

      if (!analysis || (!analysis.description && !analysis.suspectedFindings.length)) {
        throw new Error('Resposta da IA sem conteúdo aproveitável.');
      }

      setAiAnalysisSummary(analysis);
      if (!silent || !validationNotes.trim()) {
        setValidationNotes(formatAiAnalysisNotes(analysis));
      }
      if (!anomalyType && !isPhysicoChemicalAlert) setAnomalyType('other');
    } catch (err) {
      if (!silent) setError(err instanceof Error ? err.message : 'Erro ao analisar com IA');
    } finally {
      setAnalyzingWithAI(false);
    }
  };

  const handleAnalyzeWithAI = async () => {
    if (!canEditValidation) {
      setError('Seleciona o responsável e desbloqueia os campos antes de usar a IA.');
      return;
    }
    await runAiAnalysis();
  };

  const handleUnlockValidation = async () => {
    if (!selectedValidator) {
      setError('Seleciona primeiro o responsável pela validação.');
      return;
    }
    if (validationUnlocked) return;
    if (isDemoMode) {
      setValidationUnlocked(true);
      writeValidationUnlockSession(selectedValidator);
      setUnlockPromptOpen(false);
      setUnlockPassword('');
      setError(null);
      return;
    }
    if (!selectedValidator.email) {
      setError('O utilizador selecionado não tem email para revalidação.');
      return;
    }
    if (!unlockPassword.trim()) {
      setError('Indica a palavra-passe para desbloquear a validação.');
      return;
    }

    setUnlocking(true);
    setError(null);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: selectedValidator.email.trim(),
        password: unlockPassword
      });

      if (signInError) throw signInError;

      setValidationUnlocked(true);
      writeValidationUnlockSession(selectedValidator);
      setUnlockPromptOpen(false);
      setUnlockPassword('');
    } catch (unlockError: any) {
      setError(unlockError?.message ?? 'Não foi possível desbloquear a validação.');
    } finally {
      setUnlocking(false);
    }
  };

  const handleLockValidation = () => {
    setValidationUnlocked(false);
    setUnlockPromptOpen(false);
    setUnlockPassword('');
    setError(null);
    clearValidationUnlockSession();
  };

  const handleSave = async () => {
    const resolvedAnomalyType: AnomalyType | '' = isPhysicoChemicalAlert ? 'not_applicable' : anomalyType;

    if (!selectedValidator) {
      setError('Indica quem está a fazer a validação.');
      return;
    }
    if (!validationUnlocked) {
      setError('Desbloqueia os campos da validação antes de guardar.');
      return;
    }
    if (!validationResult) {
      setError('Escolhe o resultado da validação.');
      return;
    }
    if (!resolvedAnomalyType) {
      setError('Escolhe o tipo de anomalia.');
      return;
    }
    if (!severity) {
      setError('Escolhe a severidade.');
      return;
    }
    if (!validationNotes.trim()) {
      setError('Regista uma observação antes de guardar.');
      return;
    }

    setSaving(true);
    setError(null);

    const validatedAt = new Date().toISOString();
    const validatedByEmail = selectedValidator.email || undefined;
    const validatedByName = validatorDisplayName(selectedValidator);
    const validatedBy = UUID_PATTERN.test(selectedValidator.user_id) ? selectedValidator.user_id : undefined;
    const nextStatus =
      validationResult === 'confirmed_anomaly'
        ? 'confirmed'
        : validationResult === 'rejected_alert'
          ? 'rejected'
          : 'inconclusive';

    try {
      if (effectiveBulkTargets.some((target) => !target.case_id && !target.alert_id && !target.detection_id && !target.capture_id && !target.sensor_reading_id)) {
        throw new Error('Este alerta ainda não tem identificador persistido para validação operacional.');
      }

      if (isDemoMode) {
        effectiveBulkTargets.forEach((target) => saveDemoAlertValidation(target.case_id ?? target.id, {
          status: nextStatus,
          validation_result: validationResult,
          anomaly_type: resolvedAnomalyType,
          severity,
          validation_notes: validationNotes,
          validated_at: validatedAt,
          validatedByEmail
        }));
        onUpdate();
        return;
      }

      const results: Array<{ id: string; ok: boolean; error?: string }> = [];
      for (const target of effectiveBulkTargets) {
        const response = await fetch(`${functionsBaseUrl}/vision-alert-validate`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
            apikey: publicAnonKey,
            'X-Session-Token': authToken,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            case_id: target.case_id ?? null,
            alert_id: target.alert_id ?? null,
            detection_id: target.detection_id ?? null,
            capture_id: target.capture_id ?? null,
            sensor_reading_id: target.case_id ? null : target.sensor_reading_id ?? null,
            kit_id: target.kit_id ?? null,
            validated_by: validatedBy ?? null,
            validated_by_email: validatedByEmail ?? null,
            validated_by_name: validatedByName,
            validation_result: validationResult,
            anomaly_type: resolvedAnomalyType,
            severity,
            validation_notes: validationNotes,
            validated_at: validatedAt,
            validator: {
              email: validatedByEmail ?? null,
              name: validatedByName
            }
          })
        });

        const data = await parseResponsePayload(response);
        results.push({
          id: target.id,
          ok: response.ok,
          error: response.ok ? undefined : data?.error || data?.message || 'Erro ao guardar validação'
        });
      }

      const failed = results.filter((result) => !result.ok);
      if (failed.length) {
        throw new Error(`${failed.length}/${results.length} validações falharam. ${failed[0]?.error ?? ''}`.trim());
      }

      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao guardar validação');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedValidator) {
      setError('Seleciona primeiro o responsável pela validação.');
      return;
    }
    if (!validationUnlocked) {
      setError('Desbloqueia a validação com password antes de eliminar o alerta.');
      return;
    }
    if (!window.confirm('Eliminar este alerta? Esta ação não pode ser revertida.')) return;

    setDeleting(true);
    setError(null);

    try {
      if (isDemoMode) {
        if (!caseId) throw new Error('Este alerta demo não tem identificador.');
        hideDemoAlert(caseId);
        onClose();
        onUpdate();
        return;
      }

      if (!alertId && !caseId && !sensorReadingId) {
        throw new Error('Este alerta ainda não tem registo operacional associado.');
      }

      const deleteUrl = alertId
        ? `${functionsBaseUrl}/vision-alerts/${encodeURIComponent(alertId)}`
        : caseId
          ? `${functionsBaseUrl}/vision-alerts?case_id=${encodeURIComponent(caseId)}`
          : `${functionsBaseUrl}/vision-alerts?sensor_reading_id=${encodeURIComponent(sensorReadingId!)}`;
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authToken}`,
          apikey: publicAnonKey,
          'X-Session-Token': authToken
        }
      });

      const data = await parseResponsePayload(response);
      if (!response.ok) {
        throw new Error(data?.error || data?.message || 'Erro ao eliminar alerta');
      }

      onClose();
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao eliminar alerta');
    } finally {
      setDeleting(false);
    }
  };

  const dialog = (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center overscroll-contain bg-slate-950/45 p-3 backdrop-blur-sm dark:bg-slate-950/78" onClick={onClose}>
      <div
        className="flex h-[calc(100vh-1.5rem)] w-full max-w-[1800px] flex-col overflow-hidden overscroll-contain rounded-[1.75rem] border border-border bg-card text-card-foreground shadow-[0_30px_90px_-50px_rgba(15,23,42,0.68)] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:shadow-[0_40px_100px_-44px_rgba(0,0,0,0.98)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-center gap-5 border-b border-border bg-card/95 px-5 py-4 dark:border-slate-800 dark:bg-slate-950/96">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <div className="flex min-w-0 items-baseline gap-2">
                <p className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground dark:text-slate-400">Alertas</p>
                <span className="text-xs text-muted-foreground/60 dark:text-slate-600">/</span>
                <h2 className="truncate text-lg font-semibold text-card-foreground dark:text-white">{isBulkMode ? 'Validação Humana em Lote' : 'Validação Humana'}</h2>
              </div>
            </div>
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
              <HeaderChip label="Timestamp" value={formatDateTime(timestamp)} />
              <HeaderChip label="Tanque" value={tank} />
              <HeaderChip label="Espécie" value={species} />
              <HeaderChip label="Fase" value={displayPhase} />
              <HeaderChip
                label={imageUrl ? 'Marcas' : 'Captura visual'}
                value={imageUrl ? String(visualIndicatorCount) : 'Não associada'}
              />
            </div>
            <div className="flex min-w-0 flex-wrap items-center gap-2 pt-0.5">
              <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${validationStateTone[validationState]}`}>
                {validationStateLabel[validationState]}
              </span>
              <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${severityTone[severity]}`}>
                Severidade {severityLabel[severity]}
              </span>
	              <span className="inline-flex items-center rounded-full border border-cyan-200 bg-cyan-50 px-2.5 py-1 text-xs font-semibold text-cyan-800 dark:border-cyan-300/24 dark:bg-cyan-500/12 dark:text-cyan-100">
	                {isBulkMode ? `${bulkLabel ?? `${effectiveBulkTargets.length} alertas selecionados`}` : operationModeLabel}
	              </span>
              <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${
                validationUnlocked
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-300/24 dark:bg-emerald-500/12 dark:text-emerald-100'
                  : 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-300/24 dark:bg-amber-500/12 dark:text-amber-100'
              }`}>
                {validationUnlocked ? 'Validação desbloqueada' : 'Validação bloqueada'}
              </span>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground dark:text-slate-300 dark:hover:bg-white/8 dark:hover:text-white" aria-label="Fechar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-hidden overscroll-contain bg-slate-100/65 px-5 py-4 dark:bg-[#07101f]">
          <div className="grid h-full grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1.16fr)_minmax(420px,0.84fr)] xl:grid-cols-[minmax(0,1.22fr)_minmax(460px,0.78fr)]">
            <section className="grid min-h-0 grid-rows-[auto_minmax(0,1fr)_auto] gap-3 rounded-[1.35rem] border border-border bg-card p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900/55 dark:shadow-none" aria-label="Evidência visual">
              <div className="flex items-center justify-between gap-3 px-1">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs font-semibold text-cyan-700 dark:text-cyan-300">01</span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-800 dark:text-cyan-200">
                      {imageUrl ? 'Evidência visual' : 'Evidência do sensor'}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground dark:text-slate-400">
                      {imageUrl
                        ? 'Observe a captura e confirme as marcações antes de decidir.'
                        : 'Confirme a leitura, o desvio e o contexto biológico antes de decidir.'}
                    </p>
                  </div>
                </div>
                <span className="rounded-full border border-border bg-muted/60 px-2.5 py-1 font-mono text-[10px] font-semibold text-muted-foreground dark:border-slate-700 dark:bg-slate-950/55 dark:text-slate-300">
                  {imageUrl
                    ? `${visualIndicatorCount} ${visualIndicatorCount === 1 ? 'deteção' : 'deteções'}`
                    : 'Leitura ambiental'}
                </span>
              </div>
              <div className="relative min-h-0 overflow-hidden rounded-2xl border border-border bg-muted/40 dark:border-slate-800 dark:bg-slate-900/72">
                {imageUrl ? (
                  <TransformWrapper
                    initialScale={1}
                    initialPositionX={0}
                    initialPositionY={0}
                    minScale={0.75}
                    maxScale={4}
                    doubleClick={{ mode: 'reset' }}
                    wheel={{ step: 0.1 }}
                    panning={{ disabled: false }}
                  >
                    {({ zoomIn, zoomOut, resetTransform, state }) => (
                      <>
                        <div className="absolute right-3 top-3 z-10 flex gap-2 rounded-xl border border-border bg-card/90 p-2 shadow-sm backdrop-blur-md dark:border-white/10 dark:bg-slate-950/72">
                          <button type="button" onClick={() => zoomIn()} className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/40 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white" title="Aumentar zoom" aria-label="Aumentar zoom">
                            <ZoomIn className="h-4 w-4" />
                          </button>
                          <button type="button" onClick={() => zoomOut()} className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/40 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white" title="Reduzir zoom" aria-label="Reduzir zoom">
                            <ZoomOut className="h-4 w-4" />
                          </button>
                          <button type="button" onClick={() => resetTransform()} className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/40 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white" title="Repor zoom" aria-label="Repor zoom">
                            <RotateCcw className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="absolute left-3 top-3 z-10 rounded-full border border-border bg-card/90 px-3 py-1.5 text-xs font-medium text-card-foreground shadow-sm dark:border-white/10 dark:bg-slate-950/78 dark:text-white">
                          {state?.scale ? Math.round(state.scale * 100) : 100}%
                        </div>
                        <TransformComponent wrapperClass="!h-full !w-full !min-h-[44vh] md:!min-h-[52vh] xl:!min-h-0" contentClass="!flex !h-full !w-full items-center justify-center">
                          <div className="h-full w-full">
                            <img
                              src={imageUrl}
                              alt=""
                              className="hidden"
                              onLoad={(event) => setImageSize({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })}
                            />
                            {imageSize ? (
                              <svg
                                viewBox={`0 0 ${imageSize.width} ${imageSize.height}`}
                                className="h-full w-full"
                                preserveAspectRatio="xMidYMid meet"
                                aria-label="Imagem do alerta com marcações YOLO e Gemini"
                              >
                                <image href={imageUrl} width={imageSize.width} height={imageSize.height} preserveAspectRatio="none" />
                                {overlayBoxes.map((box, index) => (
                                  <g key={`${box.source}-${index}`}>
                                    <rect
                                      x={box.x * imageSize.width}
                                      y={box.y * imageSize.height}
                                      width={box.width * imageSize.width}
                                      height={box.height * imageSize.height}
                                      fill="none"
                                      stroke={box.source === 'gemini' ? '#e879f9' : '#fcd34d'}
                                      strokeWidth={4}
                                    />
                                    <rect
                                      x={box.x * imageSize.width}
                                      y={Math.max(0, box.y * imageSize.height - 28)}
                                      width={92}
                                      height={24}
                                      rx={8}
                                      fill={box.source === 'gemini' ? '#e879f9' : '#fcd34d'}
                                    />
                                    <text
                                      x={box.x * imageSize.width + 12}
                                      y={Math.max(0, box.y * imageSize.height - 12)}
                                      fontSize="11"
                                      fontWeight="700"
                                      fill="#0f172a"
                                    >
                                      {box.source === 'gemini' ? 'LLM' : 'YOLO'}
                                    </text>
                                  </g>
                                ))}
                              </svg>
                            ) : (
                              <div className="flex h-full items-center justify-center text-sm text-muted-foreground dark:text-slate-400">
                                A preparar visualização...
                              </div>
                            )}
                          </div>
                        </TransformComponent>
                      </>
                    )}
                  </TransformWrapper>
                ) : (
                  <div className="flex h-full min-h-[44vh] flex-col p-5 md:min-h-[52vh] xl:min-h-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-700 dark:text-cyan-200/80">Evidência operacional</p>
                        <h3 className="mt-1 text-lg font-semibold text-card-foreground dark:text-white">{sensorLabel}</h3>
                      </div>
                      <div className="rounded-2xl border border-cyan-200 bg-cyan-50 p-3 text-cyan-700 dark:border-cyan-300/20 dark:bg-cyan-400/10 dark:text-cyan-100" aria-hidden="true">
                        {sensorLabel === 'Temperatura' ? <Thermometer className="h-6 w-6" /> : <Activity className="h-6 w-6" />}
                      </div>
                    </div>

                    <div className="flex flex-1 items-center py-5">
                      <div className="mx-auto grid w-full max-w-3xl items-center gap-6 sm:grid-cols-[minmax(0,1fr)_minmax(220px,0.72fr)]">
                        <div className="text-center sm:text-left">
                          <p className={`font-mono text-[11px] font-semibold uppercase tracking-[0.18em] ${
                            sensorIsOutsideRange ? 'text-amber-700 dark:text-amber-200' : 'text-cyan-700 dark:text-cyan-200'
                          }`}>{sensorIsOutsideRange ? 'Valor fora do alvo' : 'Valor recebido'}</p>
                          <div className="mt-2 flex justify-center sm:justify-start">
                            <p className={`font-[950] leading-[0.86] tracking-[-0.065em] ${
                              sensorIsOutsideRange ? 'text-amber-700 dark:text-amber-300' : 'text-card-foreground dark:text-white'
                            }`} style={{ fontSize: 'clamp(5rem, 7vw, 7.5rem)' }}>
                              {sensorValue || '—'}
                              {sensorValue && sensorUnit ? (
                                <span className="ml-2 inline-block whitespace-nowrap align-baseline text-[0.28em] font-bold leading-none tracking-normal">
                                  {sensorUnit.trim()}
                                </span>
                              ) : null}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-3 border-t border-border pt-4 text-center dark:border-slate-700 sm:border-l sm:border-t-0 sm:pl-6 sm:pt-0 sm:text-left">
                          <div>
                            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground dark:text-slate-400">Alvo biológico</p>
                            <p className="mt-1 text-xl font-semibold text-card-foreground dark:text-white">
                              {biologicalRange?.label ?? (sensorThreshold ? `${sensorThreshold}${sensorUnit}` : 'A confirmar')}
                            </p>
                          </div>
                          {sensorDeviation ? (
                            <p className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1.5 text-sm font-semibold text-amber-900 dark:bg-amber-500/16 dark:text-amber-100">
                              <span className="h-2 w-2 rounded-full bg-amber-500" />
                              {formatSensorValue(sensorDeviation.value) ?? sensorDeviation.value}{sensorUnit} {sensorDeviation.direction}
                            </p>
                          ) : null}
                          {!biologicalRange && sensorDescription ? (
                            <p className="text-sm leading-6 text-muted-foreground dark:text-slate-300">{sensorDescription}</p>
                          ) : null}
                      </div>
                    </div>
                    </div>
                  </div>
                )}
              </div>

              <MetadataGrid
                items={imageUrl
                  ? [
                    { label: 'Timestamp', value: formatDateTime(timestamp) },
                    { label: 'Tanque', value: tank },
                    { label: 'Espécie', value: species },
                    { label: 'Fase', value: displayPhase },
                    { label: 'Marcação visual', value: visualIndicatorCount > 0 ? `${visualIndicatorCount} zona(s)` : 'Sem marcações' }
                  ]
                  : [
                    { label: 'Timestamp', value: formatDateTime(timestamp) },
                    { label: 'Tanque', value: tank },
                    { label: 'Espécie', value: species },
                    { label: 'Fase', value: displayPhase },
                    { label: 'Origem', value: sensorOriginLabel },
                    {
                      label: biologicalRange ? 'Alvo biológico' : 'Limite operacional',
                      value: biologicalRange?.label ?? (sensorThreshold ? `${sensorThreshold}${sensorUnit}` : 'A confirmar')
                    }
                  ]}
                columns={imageUrl ? 'sm:grid-cols-2 xl:grid-cols-5' : 'sm:grid-cols-2 xl:grid-cols-6'}
              />
            </section>

            <section className="min-h-0 overflow-y-auto overscroll-contain rounded-[1.35rem] border border-slate-300 bg-slate-200/80 p-3 pr-2 shadow-[0_16px_36px_-28px_rgba(15,23,42,0.65)] dark:border-cyan-300/20 dark:bg-slate-800/82 dark:shadow-[0_18px_44px_-30px_rgba(34,211,238,0.28)]" aria-label="Painel de decisão operacional">
              <div className="space-y-4">
                <div className="rounded-2xl bg-white p-4 shadow-sm dark:bg-slate-900 dark:shadow-none">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-700 dark:text-cyan-200/80">02 · Decisão operacional</p>
                      <h3 className="mt-1 text-base font-semibold text-card-foreground dark:text-cyan-50">Registar decisão humana</h3>
                      <p className="mt-1 text-xs text-muted-foreground dark:text-slate-400">Classifique apenas depois de rever a evidência apresentada.</p>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${validationStateTone[validationState]}`}>
                        {validationStateLabel[validationState]}
                      </span>
                      <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
                        validationUnlocked
                          ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-300/24 dark:bg-emerald-500/12 dark:text-emerald-100'
                          : 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-300/24 dark:bg-amber-500/12 dark:text-amber-100'
                      }`}>
                        {validationUnlocked ? 'Desbloqueada' : 'Bloqueada'}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 rounded-[1.15rem] bg-slate-100 p-3 shadow-inner dark:bg-slate-950/48 dark:shadow-none">
                    <div className="flex items-start gap-3">
                      <div className="rounded-xl bg-white p-2 text-cyan-700 shadow-sm dark:bg-slate-900 dark:text-cyan-100 dark:shadow-none">
                        <ShieldCheck className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-card-foreground dark:text-white">Responsável pela validação</p>
                        <p className="mt-1 text-xs leading-5 text-muted-foreground dark:text-slate-300">
                          Apenas utilizadores ativos com role de supervisor/a, operador ou administrador podem classificar alertas.
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 grid gap-2">
                      <select
                        value={validatorUserId}
                        onChange={(event) => {
                          const nextUser = validatorUsers.find((user) => user.user_id === event.target.value) ?? null;
                          setValidatorUserId(event.target.value);
                          setValidationUnlocked(isUserUnlockedInSession(nextUser));
                          setUnlockPromptOpen(false);
                          setUnlockPassword('');
                        }}
                        disabled={validatorLoading || validatorUsers.length === 0}
                        className="h-11 w-full min-w-0 rounded-xl border border-transparent bg-white px-3 py-2.5 text-sm text-foreground shadow-sm outline-none transition-colors focus:border-primary/50 focus:ring-2 focus:ring-primary/15 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500 dark:bg-slate-950/88 dark:text-slate-100 dark:shadow-none dark:scheme-dark dark:focus:border-cyan-300/60 dark:focus:ring-cyan-300/15 dark:disabled:bg-slate-900/72 dark:disabled:text-slate-300"
                      >
                        {validatorLoading ? <option value="">A carregar utilizadores...</option> : null}
                        {!validatorLoading && validatorUsers.length === 0 ? <option value="">Sem utilizadores autorizados</option> : null}
                        {validatorUsers.map((user) => (
                          <option key={user.user_id} value={user.user_id}>
                            {validatorDisplayName(user)} · {validatorRoleLabel(user.role)}
                          </option>
                        ))}
                      </select>
                      {validatorLoading ? (
                        <p className="text-xs text-muted-foreground dark:text-slate-400">A carregar equipa autorizada para validação...</p>
                      ) : null}
                      {!validatorLoading && validatorUsers.length === 0 ? (
                        <p className="text-xs text-amber-700 dark:text-amber-200">Sem utilizadores autorizados para este âmbito. Verifica perfis em configuração.</p>
                      ) : null}

                      <button
                        type="button"
                        disabled={!selectedValidator || validatorLoading}
                        onClick={() => {
                          if (validationUnlocked) return;
                          setUnlockPromptOpen(true);
                          setError(null);
                        }}
                        className={`flex min-h-16 w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left shadow-sm transition-colors disabled:cursor-not-allowed disabled:shadow-none ${
                          validationUnlocked
                            ? 'bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-500/24 dark:text-emerald-50 dark:hover:bg-emerald-500/30'
                            : 'bg-cyan-600 text-white hover:bg-cyan-700 disabled:bg-slate-200 disabled:text-slate-500 dark:bg-cyan-500/24 dark:text-cyan-50 dark:hover:bg-cyan-500/32 dark:disabled:bg-slate-900/64 dark:disabled:text-slate-400'
                        }`}
                      >
                        <span>
                          <span className="block text-sm font-semibold">
                            {validationUnlocked ? 'Validação desbloqueada' : 'Desbloquear validação'}
                          </span>
                          <span className="mt-0.5 block text-xs opacity-75">
                            {validationUnlocked ? 'Mantém-se ativa enquanto estiveres neste ecrã' : 'Pede a password do utilizador selecionado'}
                          </span>
                        </span>
                        {validationUnlocked ? <Unlock className="h-4 w-4 shrink-0" /> : <LockKeyhole className="h-4 w-4 shrink-0" />}
                      </button>

                      {validationUnlocked ? (
                        <button
                          type="button"
                          onClick={handleLockValidation}
                          className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-100 dark:border-amber-300/24 dark:bg-amber-500/10 dark:text-amber-100 dark:hover:bg-amber-500/16"
                        >
                          <LockKeyhole className="h-4 w-4" />
                          Bloquear validação nesta sessão
                        </button>
                      ) : null}
                    </div>

                  </div>

                  <div className={`transition-opacity ${!canEditValidation ? 'opacity-55' : 'opacity-100'}`} aria-disabled={!canEditValidation}>
                  <Fieldset label="Resultado da validação">
                    <SegmentedControl
                      options={validationOptions}
                      value={validationResult}
                      disabled={!canEditValidation || saving}
                      onChange={(nextValue) => {
                        setValidationResult(nextValue);
                        if (isPhysicoChemicalAlert || nextValue === 'rejected_alert') setAnomalyType('not_applicable');
                      }}
                    />
                  </Fieldset>

                  <div className="mt-4">
                    <Fieldset label="Tipo de anomalia">
                      <select
                        value={anomalyType}
                        onChange={(event) => setAnomalyType(event.target.value as AnomalyType)}
                        disabled={!canEditValidation || saving || isPhysicoChemicalAlert}
                        className="w-full rounded-xl border border-transparent bg-slate-100 px-3 py-2.5 text-sm text-foreground shadow-inner outline-none transition-colors focus:bg-white focus:ring-2 focus:ring-cyan-500/25 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500 dark:bg-slate-950/78 dark:text-slate-100 dark:shadow-none dark:scheme-dark dark:focus:bg-slate-950 dark:focus:ring-cyan-300/20 dark:disabled:bg-slate-900/62 dark:disabled:text-slate-300"
                      >
                        <option value="">Selecionar...</option>
                        {anomalyOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      {isPhysicoChemicalAlert ? (
                        <p className="text-xs text-muted-foreground dark:text-slate-400">
                          Alerta físico-químico: o tipo de anomalia fica definido automaticamente como não aplicável.
                        </p>
                      ) : null}
                    </Fieldset>

                    <Fieldset label="Severidade">
                      <SegmentedControl
                        options={severityOptions}
                        value={severity}
                        disabled={!canEditValidation || saving}
                        onChange={setSeverity}
                      />
                    </Fieldset>
                  </div>

                  <Fieldset label="Observações">
                    <textarea
                      value={validationNotes}
                      onChange={(event) => setValidationNotes(event.target.value)}
                      rows={4}
                      disabled={!canEditValidation || saving}
                      placeholder="Notas da validação, contexto do tanque ou decisão tomada..."
                      className="w-full rounded-xl border border-transparent bg-slate-100 px-3 py-3 text-sm text-foreground shadow-inner outline-none transition-colors placeholder:text-muted-foreground focus:bg-white focus:ring-2 focus:ring-cyan-500/25 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500 dark:bg-slate-950/78 dark:text-slate-100 dark:shadow-none dark:placeholder:text-slate-400 dark:focus:bg-slate-950 dark:focus:ring-cyan-300/20 dark:disabled:bg-slate-900/62 dark:disabled:text-slate-300"
                    />
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground dark:text-slate-400">
                      <span>Registo operacional obrigatório para auditoria.</span>
                      <span>{validationNotes.trim().length} caracteres</span>
                    </div>
                  </Fieldset>
                  </div>
                </div>

                <div className="space-y-3 rounded-2xl bg-white p-4 shadow-sm dark:bg-slate-900 dark:shadow-none">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-600 dark:text-slate-400">Apoio à decisão</p>
                      <h3 className="mt-1 text-sm font-semibold text-card-foreground dark:text-slate-50">Leitura visual Gemini</h3>
                      <p className="mt-1 text-xs text-muted-foreground dark:text-slate-300">
                        {aiAnalysisSummary?.validated === true ? 'Gemini confirma indícios visuais relevantes.' : 'Gemini sinalizou contexto visual para revisão humana.'}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground dark:text-slate-400">Confiança</div>
                      <div className="text-lg font-semibold text-card-foreground dark:text-white">
                        {aiAnalysisSummary?.confidence !== null && aiAnalysisSummary?.confidence !== undefined
                          ? `${Math.round(aiAnalysisSummary.confidence * 100)}%`
                          : 'N/D'}
                      </div>
                    </div>
                  </div>
                  {!authToken.startsWith('viewer_') ? (
                    <button
                      type="button"
                      onClick={handleAnalyzeWithAI}
                      disabled={analyzingWithAI || !canEditValidation}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-800 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-950/52 dark:text-slate-100 dark:hover:bg-slate-800/80"
                    >
                      {analyzingWithAI ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                      Analisar com AI
                    </button>
                  ) : null}

                  <MetadataGrid
                    items={[
                      { label: 'Indicação visual', value: visualIndicatorCount > 0 ? `${visualIndicatorCount} marcação(ões)` : 'Sem marcações visuais' },
                      { label: 'Resultado IA', value: aiAnalysisSummary?.validated === true ? 'Sinal confirmado' : aiAnalysisSummary ? 'Revisão humana necessária' : 'Sem leitura' }
                    ]}
                    columns="sm:grid-cols-2"
                  />

                  {aiAnalysisSummary?.description ? (
                    <div className="rounded-xl bg-slate-100 p-3 text-sm leading-6 text-card-foreground dark:bg-slate-950/40 dark:text-slate-100">
                      {aiAnalysisSummary.description}
                    </div>
                  ) : (
                    <div className="rounded-xl bg-slate-100 p-3 text-sm text-muted-foreground dark:bg-slate-950/40 dark:text-slate-400">
                      Ainda não existe leitura visual Gemini para este alerta.
                    </div>
                  )}

                  {aiAnalysisSummary?.suspectedFindings.length ? (
                    <div>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground dark:text-slate-400">Achados sugeridos</p>
                      <div className="flex flex-wrap gap-2">
                        {aiAnalysisSummary.suspectedFindings.map((finding) => (
                          <span key={finding} className="rounded-full border border-border bg-card px-2.5 py-1 text-xs font-medium text-card-foreground dark:border-white/10 dark:bg-slate-950/28 dark:text-slate-100">
                            {finding}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {aiAnalysisSummary?.analysisTags?.length ? (
                    <div>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground dark:text-slate-400">Tags visuais</p>
                      <div className="flex flex-wrap gap-2">
                        {aiAnalysisSummary.analysisTags.map((tag) => (
                          <span key={tag} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-950/44 dark:text-slate-200">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {aiAnalysisSummary?.operationalAssessment ? (
                    <div className="rounded-xl border border-cyan-200 bg-cyan-50/70 p-3 text-sm leading-6 text-cyan-900 dark:border-cyan-300/20 dark:bg-cyan-400/10 dark:text-cyan-50">
                      {aiAnalysisSummary.operationalAssessment}
                    </div>
                  ) : null}

                  {aiAnalysisSummary?.recommendedNextSteps ? (
                    <div className="text-sm leading-6 text-muted-foreground dark:text-slate-300">
                      <span className="font-semibold text-card-foreground dark:text-slate-100">Próximo passo:</span> {aiAnalysisSummary.recommendedNextSteps}
                    </div>
                  ) : null}
                </div>

                {hasPreviousValidation ? (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-400/24 dark:bg-emerald-500/10 dark:text-emerald-100">
                    Validação anterior: {previousValidatorLabel} em {formatDateTime(alert.validated_at ?? alert.validatedAt)}
                  </div>
                ) : null}
              </div>
            </section>
          </div>

        </div>

        {error ? (
          <div className="mx-6 mb-3 flex shrink-0 items-start gap-2 rounded-xl bg-red-50 px-3 py-2.5 text-red-800 shadow-sm dark:bg-red-500/12 dark:text-red-100" role="alert" aria-live="polite">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-300" />
            <p className="text-sm leading-5">{error}</p>
          </div>
        ) : null}

        {unlockPromptOpen && !validationUnlocked ? (
          <div
            className="fixed inset-0 z-90 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => {
              if (unlocking) return;
              setUnlockPromptOpen(false);
              setUnlockPassword('');
              setError(null);
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="unlock-validation-title"
              className="w-full max-w-md rounded-2xl bg-card p-4 text-card-foreground shadow-[0_30px_90px_rgba(0,0,0,0.45)] dark:bg-slate-900"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-amber-100 p-2 text-amber-700 dark:bg-amber-500/18 dark:text-amber-100">
                  <LockKeyhole className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p id="unlock-validation-title" className="text-sm font-semibold text-card-foreground dark:text-white">
                    Confirmar desbloqueio
                  </p>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground dark:text-slate-400">
                    Introduz a password de <span className="font-semibold text-card-foreground dark:text-slate-200">{selectedValidator ? validatorDisplayName(selectedValidator) : 'utilizador'}</span> para editar este alerta.
                  </p>
                </div>
              </div>

              <input
                type="password"
                value={unlockPassword}
                onChange={(event) => setUnlockPassword(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    if (unlocking) return;
                    setUnlockPromptOpen(false);
                    setUnlockPassword('');
                    setError(null);
                  }
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void handleUnlockValidation();
                  }
                }}
                autoFocus
                placeholder="Palavra-passe"
                className="mt-4 h-11 w-full rounded-xl border border-transparent bg-slate-100 px-3 py-2.5 text-sm text-foreground shadow-inner outline-none placeholder:text-muted-foreground focus:bg-white focus:ring-2 focus:ring-cyan-500/25 dark:bg-slate-950/72 dark:text-white dark:placeholder:text-slate-500 dark:focus:bg-slate-950 dark:focus:ring-cyan-300/20"
              />

              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => {
                    setUnlockPromptOpen(false);
                    setUnlockPassword('');
                    setError(null);
                  }}
                  disabled={unlocking}
                  className="rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-800 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-950/60 dark:text-slate-100 dark:hover:bg-slate-800"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => void handleUnlockValidation()}
                  disabled={unlocking || !selectedValidator}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-cyan-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:bg-cyan-500/24 dark:text-cyan-50 dark:hover:bg-cyan-500/32 dark:disabled:bg-slate-950 dark:disabled:text-slate-600"
                >
                  {unlocking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />}
                  Desbloquear
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-3 border-t border-slate-200 bg-card px-5 py-4 dark:border-slate-800 dark:bg-slate-950/96 sm:flex-row sm:items-center">
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-cyan-700 dark:text-cyan-300">03 · Registo</p>
            <p className="mt-1 text-xs text-muted-foreground dark:text-slate-400">
              {saveDisabledReason ?? 'Validação pronta para gravação.'}
            </p>
            {!isBulkMode && !authToken.startsWith('viewer_') && deleteDisabledReason ? (
              <p className="mt-1 text-[11px] text-muted-foreground dark:text-slate-500">{deleteDisabledReason}</p>
            ) : null}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
          <button
            onClick={onClose}
            disabled={saving || deleting}
            className="min-w-32 rounded-xl border border-border bg-card px-4 py-3 font-medium text-slate-800 transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving || deleting || !canEditValidation}
            className="inline-flex min-w-52 items-center justify-center gap-2 rounded-xl bg-cyan-600 px-5 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-cyan-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600 disabled:shadow-none dark:bg-cyan-500/28 dark:text-cyan-50 dark:hover:bg-cyan-500/38 dark:disabled:bg-slate-700 dark:disabled:text-slate-300"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
	            {isBulkMode ? `Aplicar a ${effectiveBulkTargets.length} alertas` : 'Guardar validação'}
	          </button>
	          {!isBulkMode && !authToken.startsWith('viewer_') ? (
            <button
              onClick={handleDelete}
              disabled={saving || deleting || !validationUnlocked || !selectedValidator}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-transparent px-4 py-3 font-medium text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400 dark:border-red-400/20 dark:text-red-200 dark:hover:bg-red-500/10 dark:disabled:border-slate-800 dark:disabled:text-slate-600"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Eliminar alerta
            </button>
          ) : null}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
}

function MetadataGrid({
  items,
  columns
}: {
  items: Array<{ label: string; value: string }>;
  columns: string;
}) {
  return (
    <div className={`grid ${columns} rounded-[1.15rem] bg-slate-100/70 px-4 py-3 dark:bg-slate-950/32`}>
      {items.map((item, index) => (
        <MetadataItem key={item.label} label={item.label} value={item.value} showDivider={index > 0} />
      ))}
    </div>
  );
}

function MetadataItem({ label, value, showDivider }: { label: string; value: string; showDivider?: boolean }) {
  return (
    <div className={`min-w-0 py-2 ${showDivider ? 'border-t border-slate-200/70 dark:border-slate-800 sm:border-l sm:border-t-0 sm:pl-4' : ''}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground dark:text-slate-400">{label}</p>
      <p className="mt-1 truncate text-sm font-semibold text-card-foreground dark:text-slate-100">{value}</p>
    </div>
  );
}

function HeaderChip({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex min-w-0 items-baseline gap-1 text-xs">
      <span className="text-muted-foreground dark:text-slate-500">{label}</span>
      <span className="max-w-56 truncate font-medium text-card-foreground/88 dark:text-slate-200">{value}</span>
    </span>
  );
}

function SegmentedControl<TValue extends string>({
  options,
  value,
  disabled,
  onChange
}: {
  options: Array<{ value: TValue; label: string }>;
  value: TValue | '';
  disabled?: boolean;
  onChange: (value: TValue) => void;
}) {
  const activeIndex = Math.max(0, options.findIndex((option) => option.value === value));
  const gridClass = segmentedGridCols[options.length] ?? 'grid-cols-1';
  const gapRem = 0.375;
  const horizontalPaddingRem = 0.75;
  const activeWidth = `calc((100% - ${horizontalPaddingRem}rem - ${(options.length - 1) * gapRem}rem) / ${options.length})`;
  const activeSurface = value === 'confirmed_anomaly'
    ? 'bg-amber-100 ring-amber-500/45 dark:bg-amber-500/24 dark:ring-amber-300/35'
    : value === 'rejected_alert'
      ? 'bg-emerald-100 ring-emerald-500/35 dark:bg-emerald-500/20 dark:ring-emerald-300/30'
      : 'bg-cyan-50 ring-cyan-500/30 dark:bg-cyan-500/18 dark:ring-cyan-300/24';

  return (
    <div className={`relative grid ${gridClass} gap-1.5 rounded-[1.1rem] bg-slate-100 p-1.5 dark:bg-slate-950/48`} role="radiogroup" aria-disabled={disabled ? 'true' : 'false'}>
      {value ? (
        <span
          aria-hidden="true"
          className={`absolute bottom-1.5 left-1.5 top-1.5 rounded-[0.9rem] shadow-sm ring-1 transition-transform duration-300 ease-out motion-reduce:transition-none ${activeSurface}`}
          style={{
            width: activeWidth,
            transform: `translateX(calc(${activeIndex} * (100% + ${gapRem}rem)))`
          }}
        />
      ) : null}
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => {
              if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
              event.preventDefault();
              const currentIndex = options.findIndex((item) => item.value === value);
              const fallbackIndex = currentIndex >= 0 ? currentIndex : 0;
              const nextIndex = event.key === 'ArrowRight'
                ? (fallbackIndex + 1) % options.length
                : (fallbackIndex - 1 + options.length) % options.length;
              onChange(options[nextIndex].value);
            }}
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            className={`relative z-10 rounded-[0.9rem] px-3 py-2.5 text-sm font-medium transition-colors duration-200 disabled:cursor-not-allowed ${
              selected
                ? value === 'confirmed_anomaly'
                  ? 'text-amber-950 dark:text-amber-50'
                  : 'text-slate-950 dark:text-slate-50'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white'
            } disabled:text-slate-500 dark:disabled:text-slate-400`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function Fieldset({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mt-4 space-y-2">
      <label className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground dark:text-cyan-100/80">{label}</label>
      {children}
    </div>
  );
}
